import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StockShareType } from 'apps/user/types/on-boarding.type';
import { AxiosError } from 'axios';
import { Alert } from 'components/Toast';
import { toast } from 'react-toastify';
import { services } from 'service';
import {
  AssignTrusteeDto,
  BeneficiariesAllocationDto
} from 'service/dto/general.dto';
import { AssignBeneficiaryDto, StockShare } from 'service/dto/stock-share.dto';

export const useCreateStock = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (stockShare: StockShareType): Promise<void> => {
      return services.stockService.createStock(stockShare);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['create-stock']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetStockList = () =>
  useQuery(['stocks'], () => services.stockService.getStocks(), {
    refetchOnWindowFocus: false
  });

export const useDeleteStock = () =>
  useMutation((stockId: number) => services.stockService.deleteStock(stockId), {
    onError: (error: Error) => {
      toast.error(<Alert message={error.message} type="error" />);
    }
  });

export const useAssignStockBeneficiary = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (assignBeneficiaryDto: AssignBeneficiaryDto): Promise<void> => {
      return services.stockService.assignBeneficiary(assignBeneficiaryDto);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assign-stock-beneficiary']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetStockDetails = ({ stockId }: { stockId: number }) => {
  return useQuery<StockShare, AxiosError<Error>>(
    ['get_stock', stockId],
    () => services.stockService.getStock(stockId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!stockId
    }
  );
};

export const useUnAssignStockBeneficiary = () =>
  useMutation(
    ({ allocationId }: { allocationId?: number }) =>
      services.stockService.unAssignStockBeneficiary(allocationId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useStockAllocationBulkEdit = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (beneficiariesAllocationDto: BeneficiariesAllocationDto): Promise<void> => {
      return services.stockService.editBulkStockBeneficiary(
        beneficiariesAllocationDto
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['stock-allocation-edit']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useRemoveStockTrustee = () =>
  useMutation(
    ({ assetId, trusteeId }: { assetId: number; trusteeId: number }) =>
      services.stockService.removeStockTrustee(assetId, trusteeId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useAssignStockTrustee = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ assetId, trusteeId, note }: AssignTrusteeDto): Promise<void> => {
      return services.stockService.assignStockTrustee(assetId, trusteeId, note);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assign-stock-trustee']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};
